/**
 * src/pages/AnalyticsReport.js
 * Single report viewer — funnel visualization, entity list, journey timeline.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../context/PlanContext';
import UpgradeBanner from '../components/UpgradeBanner';
import FunnelChart from '../components/FunnelChart';
import TimeSeriesChart from '../components/TimeSeriesChart';
import DurationChart from '../components/DurationChart';
import ActiveChart from '../components/ActiveChart';
import ComparisonChart from '../components/ComparisonChart';
import api from '../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDurationSeconds(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return sec % 60 === 0 ? `${Math.floor(sec / 60)}m` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (sec < 86400) return m === 0 ? `${h}h` : `${h}h ${m}m`;
  const d = Math.floor(sec / 86400);
  const rh = Math.floor((sec % 86400) / 3600);
  return rh === 0 ? `${d}d` : `${d}d ${rh}h`;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 0) return '';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return '';
}

// ---------------------------------------------------------------------------
// Entity List
// ---------------------------------------------------------------------------

function EntityList({ reportId, stepOrder, from, to, configVersion }) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [journeyEntity, setJourneyEntity] = useState(null);
  const [sortBy, setSortBy] = useState('first_seen');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stepFilters, setStepFilters] = useState({});

  // Reset step filters when stepOrder changes
  useEffect(() => {
    if (stepOrder?.length) {
      const initial = {};
      for (const s of stepOrder) initial[s.step_id] = 'any';
      setStepFilters(initial);
    }
  }, [stepOrder]);

  function handleSort(col) {
    if (sortBy === col) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setCursor(null);
  }

  function cycleStepFilter(stepId) {
    setStepFilters(prev => {
      const current = prev[stepId] || 'any';
      const next = current === 'any' ? 'completed' : current === 'completed' ? 'not_completed' : 'any';
      return { ...prev, [stepId]: next };
    });
  }

  const hasActiveStepFilters = Object.values(stepFilters).some(v => v !== 'any');

  const fetchEntities = useCallback(async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);
      params.set('filter', filter);
      params.set('limit', '50');
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      if (append && cursor) params.set('cursor', cursor);

      const res = await api.get(`/portal/analytics/reports/${reportId}/entities?${params}`);
      if (append) {
        setEntities(prev => [...prev, ...res.data.data]);
      } else {
        setEntities(res.data.data);
      }
      setHasMore(res.data.pagination.has_more);
      setCursor(res.data.pagination.next_cursor);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.error === 'feature_not_available') {
        setError('__gated__');
      } else {
        setError(err.response?.data?.message || 'Failed to load entities.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [reportId, from, to, configVersion, filter, sortBy, sortOrder, cursor]);

  useEffect(() => {
    setCursor(null);
    fetchEntities(false);
  }, [reportId, from, to, configVersion, filter, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error === '__gated__') return null; // handled by parent UpgradeBanner

  // Apply client-side step filters
  const filteredEntities = entities.filter(entity => {
    return Object.entries(stepFilters).every(([stepId, filterVal]) => {
      if (filterVal === 'any') return true;
      const completed = entity.step_details?.[stepId]?.completed;
      return filterVal === 'completed' ? completed : !completed;
    });
  });

  function renderSortArrow(col) {
    if (sortBy !== col) return null;
    return <span className="sort-arrow">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  }

  return (
    <div>
      {/* Journey overlay */}
      {journeyEntity && (
        <JourneyTimeline
          reportId={reportId}
          entityValue={journeyEntity}
          onClose={() => setJourneyEntity(null)}
        />
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {['all', 'complete', 'incomplete'].map(f => (
          <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setFilter(f); setCursor(null); }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        {hasActiveStepFilters && (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px' }}>
            Step filters active
            {' · '}
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '12px', padding: '0 4px' }}
              onClick={() => {
                const reset = {};
                for (const s of stepOrder) reset[s.step_id] = 'any';
                setStepFilters(reset);
              }}>Clear</button>
          </span>
        )}
      </div>

      {error && error !== '__gated__' && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '12px 0' }}>Loading entities…</div>}

      {!loading && entities.length === 0 && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '12px 0' }}>
          No entities found for the current filters.
        </div>
      )}

      {!loading && entities.length > 0 && filteredEntities.length === 0 && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '12px 0' }}>
          No entities match the selected step filters.
        </div>
      )}

      {!loading && filteredEntities.length > 0 && (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('entity_id')}>
                  Entity ID {renderSortArrow('entity_id')}
                </th>
                <th className="th-sortable" onClick={() => handleSort('first_seen')}>
                  First Seen {renderSortArrow('first_seen')}
                </th>
                {(stepOrder || []).map(s => {
                  const fv = stepFilters[s.step_id] || 'any';
                  return (
                    <th key={s.step_id} className="th-sortable" style={{ textAlign: 'center' }}
                      onClick={() => cycleStepFilter(s.step_id)}
                      title={`Click to filter: ${fv === 'any' ? 'showing all' : fv === 'completed' ? 'completed only' : 'not completed only'}`}>
                      {s.label}
                      {fv === 'completed' && <span style={{ color: 'var(--success)', marginLeft: '4px' }}>✓</span>}
                      {fv === 'not_completed' && <span style={{ color: 'var(--danger)', marginLeft: '4px' }}>✗</span>}
                    </th>
                  );
                })}
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntities.map((entity, i) => (
                <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setJourneyEntity(entity.entity_id)}>
                  <td className="td-mono" style={{ fontSize: '12px' }}>{entity.entity_id}</td>
                  <td className="td-mono" style={{ fontSize: '12px' }}>
                    {formatTimestamp(entity.first_seen)}
                    {formatRelativeTime(entity.first_seen) && (
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                        {formatRelativeTime(entity.first_seen)}
                      </div>
                    )}
                  </td>
                  {(stepOrder || []).map(s => {
                    const detail = entity.step_details?.[s.step_id];
                    return (
                      <td key={s.step_id} style={{ textAlign: 'center' }}>
                        {detail?.completed ? (
                          <span style={{ color: 'var(--success)', fontWeight: '600' }}>
                            &#10003;{detail.event_count > 1 ? `(${detail.event_count})` : ''}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                  <td>
                    <span style={{
                      fontSize: '12px', fontWeight: '600',
                      color: entity.steps_completed === entity.total_steps ? 'var(--success)' : 'var(--text-secondary)'
                    }}>
                      {entity.steps_completed}/{entity.total_steps}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <button className="btn btn-ghost" style={{ marginTop: '8px', width: '100%' }}
          onClick={() => fetchEntities(true)} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Journey Timeline
// ---------------------------------------------------------------------------

function JourneyTimeline({ reportId, entityValue, transitionIndex, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchJourney() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ entity_value: entityValue });
        if (transitionIndex != null) params.set('transition_index', transitionIndex);
        const res = await api.get(`/portal/analytics/reports/${reportId}/journey?${params}`);
        setEvents(res.data.events || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load journey.');
      } finally {
        setLoading(false);
      }
    }
    fetchJourney();
  }, [reportId, entityValue, transitionIndex]);

  return (
    <div className="card" style={{ marginBottom: '16px', border: '2px solid var(--accent)' }}>
      <div className="card-header">
        <span className="card-title">Journey: {entityValue}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </div>
      <div className="card-body">
        {loading && <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading journey…</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {!loading && events.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No events found for this entity.</div>
        )}

        {!loading && events.length > 0 && (
          <div className="analytics-journey">
            {events.map((event, i) => (
              <div key={event.event_id} className="analytics-journey-event">
                <div className="analytics-journey-dot" />
                {i < events.length - 1 && <div className="analytics-journey-line" />}
                <div className="analytics-journey-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>{event.step_label || event.step_id}</span>
                    <span className="badge badge-neutral" style={{ fontSize: '10px' }}>{event.process_name || event.process_id}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {formatTimestamp(event.captured_at)}
                  </div>
                  {event.application_name && (
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                      {event.application_name} — {event.page_title || event.url}
                    </div>
                  )}
                  {event.identifiers && Object.keys(event.identifiers).length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                      {Object.entries(event.identifiers).map(([key, val]) => (
                        <span key={key} style={{
                          fontSize: '11px', padding: '1px 6px', borderRadius: '3px',
                          background: 'var(--accent-subtle)', color: 'var(--accent)'
                        }}>
                          {key}: {val}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version History Panel
// ---------------------------------------------------------------------------

function VersionHistory({ reportId, onRevert, isAdmin }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState(null);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const res = await api.get(`/portal/analytics/reports/${reportId}/versions`);
        setVersions(res.data);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [reportId]);

  async function handleRevert(versionNumber) {
    setReverting(versionNumber);
    try {
      await api.post(`/portal/analytics/reports/${reportId}/revert`, { version_number: versionNumber });
      onRevert();
    } catch {
      // Error handled by parent
    } finally {
      setReverting(null);
    }
  }

  if (loading) return <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Loading versions…</div>;

  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Version</th>
            <th>Note</th>
            <th>Created</th>
            {isAdmin && <th style={{ width: '80px' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {versions.map((v, i) => (
            <tr key={v.version_number}>
              <td>
                <span style={{ fontWeight: i === 0 ? '600' : '400' }}>v{v.version_number}</span>
                {i === 0 && <span className="badge badge-success" style={{ marginLeft: '6px', fontSize: '10px' }}>current</span>}
              </td>
              <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{v.change_note || '—'}</td>
              <td className="td-mono" style={{ fontSize: '12px' }}>{formatTimestamp(v.created_at)}</td>
              {isAdmin && (
                <td>
                  {i > 0 && (
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => handleRevert(v.version_number)}
                      disabled={reverting === v.version_number}>
                      {reverting === v.version_number ? 'Reverting…' : 'Revert'}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breach List (duration reports)
// ---------------------------------------------------------------------------

function BreachList({ reportId, transitionIndex, transition, from, to, configVersion }) {
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [thresholdFilter, setThresholdFilter] = useState('breach');
  const [sortBy, setSortBy] = useState('duration');
  const [sortOrder, setSortOrder] = useState('desc');
  const [journeyEntity, setJourneyEntity] = useState(null);

  const fetchBreaches = useCallback(async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('transition_index', transitionIndex);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);
      params.set('filter', thresholdFilter);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('limit', '50');
      if (append && cursor) params.set('cursor', cursor);

      const res = await api.get(`/portal/analytics/reports/${reportId}/duration/breaches?${params}`);
      if (append) {
        setBreaches(prev => [...prev, ...res.data.data]);
      } else {
        setBreaches(res.data.data || []);
      }
      setHasMore(res.data.pagination?.has_more || false);
      setCursor(res.data.pagination?.next_cursor || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load breach data.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [reportId, transitionIndex, from, to, configVersion, thresholdFilter, sortBy, sortOrder, cursor]);

  useEffect(() => {
    setCursor(null);
    fetchBreaches(false);
  }, [reportId, transitionIndex, from, to, configVersion, thresholdFilter, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSort(col) {
    if (sortBy === col) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setCursor(null);
  }

  function renderSortArrow(col) {
    if (sortBy !== col) return null;
    return <span className="sort-arrow">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  }

  function eventsLink() {
    return '/events';
  }

  return (
    <div>
      {/* Journey overlay */}
      {journeyEntity && (
        <JourneyTimeline
          reportId={reportId}
          entityValue={journeyEntity}
          transitionIndex={transitionIndex}
          onClose={() => setJourneyEntity(null)}
        />
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', alignItems: 'center' }}>
        {['breach', 'warning', 'all'].map(f => (
          <button key={f} className={`btn btn-sm ${thresholdFilter === f ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setThresholdFilter(f); setCursor(null); }}>
            {f === 'breach' ? 'Breach' : f === 'warning' ? 'Warning' : 'All'}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error" style={{ fontSize: '13px' }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '8px 0' }}>Loading…</div>}

      {!loading && breaches.length === 0 && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '8px 0' }}>
          No {thresholdFilter === 'all' ? '' : thresholdFilter + ' '}entities found.
        </div>
      )}

      {!loading && breaches.length > 0 && (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('entity_id')}>
                  Entity {renderSortArrow('entity_id')}
                </th>
                <th className="th-sortable" onClick={() => handleSort('duration')}>
                  Duration {renderSortArrow('duration')}
                </th>
                <th>Excess</th>
                <th>Status</th>
                <th style={{ width: '80px' }}></th>
              </tr>
            </thead>
            <tbody>
              {breaches.map((b, i) => (
                <tr key={i}>
                  <td className="td-mono" style={{ fontSize: '12px', cursor: 'pointer', color: 'var(--accent)' }}
                    onClick={() => setJourneyEntity(b.entity_id)}>
                    {b.entity_id}
                  </td>
                  <td className="td-mono" style={{ fontSize: '12px' }}>{b.duration_formatted}</td>
                  <td className="td-mono" style={{ fontSize: '12px', color: 'var(--danger)' }}>
                    +{b.breach_excess_formatted}
                  </td>
                  <td>
                    <span className={`badge ${b.status === 'breach' ? 'badge-danger' : 'badge-warning'}`}
                      style={{ fontSize: '10px' }}>
                      {b.status}
                    </span>
                  </td>
                  <td>
                    <Link to={eventsLink(b.entity_id)}
                      style={{ fontSize: '11px', color: 'var(--accent)', textDecoration: 'none' }}>
                      Events →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <button className="btn btn-ghost" style={{ marginTop: '8px', width: '100%' }}
          onClick={() => fetchBreaches(true)} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active / WIP Entity List
// ---------------------------------------------------------------------------

function ActiveEntityList({ reportId, activeData, from, to, configVersion, onFetchMore }) {
  const [sortBy, setSortBy] = useState('waiting_since');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stepFilter, setStepFilter] = useState('');
  const [staleOnly, setStaleOnly] = useState(false);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [journeyEntity, setJourneyEntity] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const fetchEntities = useCallback(async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('limit', '50');
      if (stepFilter !== '') params.set('step_index', stepFilter);
      if (staleOnly) params.set('stale_only', 'true');
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);
      if (append && cursor) params.set('cursor', cursor);

      const res = await api.get(`/portal/analytics/reports/${reportId}/active?${params}`);
      const data = res.data;

      if (append) {
        setEntities(prev => [...prev, ...data.entities]);
      } else {
        setEntities(data.entities);
      }
      setHasMore(data.pagination.has_more);
      setCursor(data.pagination.next_cursor);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load active entities.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [reportId, sortBy, sortOrder, stepFilter, staleOnly, from, to, configVersion, cursor]);

  useEffect(() => {
    fetchEntities(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, sortBy, sortOrder, stepFilter, staleOnly, from, to, configVersion]);

  async function fetchJourney(entityId) {
    if (journeyEntity === entityId) {
      setJourneyEntity(null);
      setJourneyData(null);
      return;
    }
    setJourneyEntity(entityId);
    setJourneyLoading(true);
    try {
      const res = await api.get(`/portal/analytics/reports/${reportId}/journey?entity_value=${encodeURIComponent(entityId)}`);
      setJourneyData(res.data);
    } catch {
      setJourneyData(null);
    } finally {
      setJourneyLoading(false);
    }
  }

  function toggleSort(col) {
    if (sortBy === col) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
  }

  const steps = activeData?.summary?.by_step || [];
  const sortArrow = sortOrder === 'asc' ? ' ▲' : ' ▼';

  return (
    <div>
      {/* Filters row */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ fontSize: '11px' }}>Step</label>
          <select className="form-select" value={stepFilter}
            onChange={e => { setStepFilter(e.target.value); setCursor(null); }}
            style={{ width: '180px' }}>
            <option value="">All steps</option>
            {steps.map(s => (
              <option key={s.step_index} value={s.step_index}>{s.label} ({s.active_count})</option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', marginTop: '16px' }}>
          <input type="checkbox" checked={staleOnly} onChange={e => { setStaleOnly(e.target.checked); setCursor(null); }} />
          Stale only
        </label>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading entities…</div>
      ) : entities.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No active entities found.</div>
      ) : (
        <>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('entity_id')}>
                    Entity{sortBy === 'entity_id' ? sortArrow : ''}
                  </th>
                  <th>Current Step</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('waiting_since')}>
                    Wait Time{sortBy === 'waiting_since' ? sortArrow : ''}
                  </th>
                  <th>Waiting Since</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entities.map(entity => (
                  <React.Fragment key={entity.entity_id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => fetchJourney(entity.entity_id)}>
                      <td className="td-mono" style={{ fontSize: '12px' }}>{entity.entity_id}</td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          fontSize: '12px', padding: '2px 8px', borderRadius: '10px',
                          background: 'var(--accent-subtle)', color: 'var(--accent)'
                        }}>
                          {entity.current_step_label}
                        </span>
                      </td>
                      <td className="td-mono" style={{ fontSize: '12px', fontWeight: entity.stale ? '600' : '400' }}>
                        {entity.waiting_formatted}
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {formatTimestamp(entity.waiting_since)}
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                          {formatRelativeTime(entity.waiting_since)}
                        </div>
                      </td>
                      <td>
                        {entity.stale ? (
                          <span className="badge badge-warning" style={{ fontSize: '11px' }}>Stale</span>
                        ) : (
                          <span className="badge badge-success" style={{ fontSize: '11px' }}>Active</span>
                        )}
                      </td>
                    </tr>
                    {/* Inline journey */}
                    {journeyEntity === entity.entity_id && (
                      <tr>
                        <td colSpan="5" style={{ padding: '12px 16px', background: 'var(--bg-secondary)' }}>
                          {journeyLoading ? (
                            <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Loading journey…</div>
                          ) : journeyData?.events?.length > 0 ? (
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-secondary)' }}>
                                Journey — {journeyData.events.length} events
                              </div>
                              {journeyData.events.map((evt, i) => (
                                <div key={evt.event_id || i} style={{
                                  display: 'flex', gap: '12px', alignItems: 'flex-start',
                                  padding: '6px 0', borderBottom: i < journeyData.events.length - 1 ? '1px solid var(--border)' : 'none'
                                }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent)', marginTop: '4px', flexShrink: 0 }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600' }}>{evt.step_label || evt.step_id}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                      {formatTimestamp(evt.captured_at)}
                                      {evt.url && <span> · {evt.url}</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No journey events found.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => fetchEntities(true)} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Report View
// ---------------------------------------------------------------------------

export default function AnalyticsReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const { plan, hasFeature } = usePlan();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Chart data (funnel, time-series, or duration)
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [tsData, setTsData] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [durationData, setDurationData] = useState(null);
  const [durationLoading, setDurationLoading] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [showBreaches, setShowBreaches] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeStepFilter, setActiveStepFilter] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [configVersion, setConfigVersion] = useState('');
  const [configVersions, setConfigVersions] = useState([]);

  // UI toggles
  const [showVersions, setShowVersions] = useState(false);
  const [showEntities, setShowEntities] = useState(true);

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchReport = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.get(`/portal/analytics/reports/${id}`);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFunnel = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);

      const res = await api.get(`/portal/analytics/reports/${id}/funnel?${params}`);
      setFunnel(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load funnel data.');
    } finally {
      setFunnelLoading(false);
    }
  }, [id, from, to, configVersion]);

  const fetchTimeSeries = useCallback(async () => {
    setTsLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);

      const res = await api.get(`/portal/analytics/reports/${id}/time-series?${params}`);
      setTsData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load time-series data.');
    } finally {
      setTsLoading(false);
    }
  }, [id, from, to, configVersion]);

  const fetchDuration = useCallback(async () => {
    setDurationLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);

      const res = await api.get(`/portal/analytics/reports/${id}/duration?${params}`);
      setDurationData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load duration data.');
    } finally {
      setDurationLoading(false);
    }
  }, [id, from, to, configVersion]);

  const fetchActive = useCallback(async () => {
    setActiveLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (configVersion) params.set('config_version', configVersion);
      params.set('limit', '0'); // summary only for the chart card

      const res = await api.get(`/portal/analytics/reports/${id}/active?${params}`);
      setActiveData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load active/WIP data.');
    } finally {
      setActiveLoading(false);
    }
  }, [id, from, to, configVersion]);

  const fetchComparison = useCallback(async () => {
    setComparisonLoading(true);
    try {
      const res = await api.get(`/portal/analytics/reports/${id}/comparison`);
      setComparisonData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load comparison data.');
    } finally {
      setComparisonLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => {
    if (!report) return;
    const type = report.definition?.type || 'funnel';
    if (type === 'duration') fetchDuration();
    else if (type === 'time_series') fetchTimeSeries();
    else if (type === 'active') fetchActive();
    else if (type === 'comparison') fetchComparison();
    else fetchFunnel();
  }, [report, fetchFunnel, fetchTimeSeries, fetchDuration, fetchActive, fetchComparison]);
  useEffect(() => {
    api.get('/portal/analytics/config-versions')
      .then(res => setConfigVersions(res.data.versions || []))
      .catch(() => {});
  }, []);

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/portal/analytics/reports/${id}`);
      navigate('/analytics');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete report.');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  if (error && !report) return (
    <div className="page">
      <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
      <div className="alert alert-error" style={{ marginTop: '12px' }}>
        {error}
        <button className="alert-retry" onClick={fetchReport}>Try again</button>
      </div>
    </div>
  );

  if (!report) return null;

  const def = report.definition || {};
  const isTimeSeries = def.type === 'time_series';
  const isDuration = def.type === 'duration';
  const isActive = def.type === 'active';
  const isComparison = def.type === 'comparison';
  const hasTimeMetrics = !isTimeSeries && !isDuration && !isActive && !isComparison && def.metrics?.time_between_steps?.enabled;

  // Step order from funnel data
  const stepOrder = funnel?.steps?.map(s => ({ step_id: s.step_id, label: s.label })) || [];

  // Total events for time-series summary
  const tsTotalEvents = tsData?.series
    ? tsData.series.reduce((sum, s) => sum + s.data.reduce((acc, d) => acc + d.count, 0), 0)
    : 0;

  // Selected transition detail for duration reports
  const selectedTrans = durationData?.transitions?.[selectedTransition];

  const fetchData = isComparison ? fetchComparison : isDuration ? fetchDuration : isTimeSeries ? fetchTimeSeries : isActive ? fetchActive : fetchFunnel;

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: '4px' }}>
        <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
      </div>

      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">{report.name}</h1>
          {report.description && <p className="page-subtitle">{report.description}</p>}
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            {isDuration ? (
              <>
                {def.transitions?.length || 0} transition{(def.transitions?.length || 0) !== 1 ? 's' : ''}
                {' · '}v{report.version_number}
              </>
            ) : isTimeSeries ? (
              <>
                {def.series?.length || 0} series · {def.granularity} · {def.display?.chart_type || 'line'} chart
                {' · '}v{report.version_number}
              </>
            ) : isActive ? (
              <>
                {def.steps?.length || 0} steps · stale threshold: {def.stale_threshold?.value || 3}{def.stale_threshold?.unit === 'hours' ? 'h' : 'd'}
                {' · '}v{report.version_number}
              </>
            ) : isComparison ? (
              <>
                {def.base_type === 'funnel' ? 'Funnel' : 'Time Series'} comparison
                {' · '}v{report.version_number}
              </>
            ) : (
              <>
                {def.steps?.length || 0} steps across {new Set(def.steps?.map(s => s.process_id)).size} process{new Set(def.steps?.map(s => s.process_id)).size !== 1 ? 'es' : ''}
                {' · '}v{report.version_number}
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className={`btn btn-sm ${showVersions ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setShowVersions(v => !v)}>
            Version History
          </button>
          {isAdmin && (
            <>
              <Link to={`/analytics/edit/${report.id}`} className="btn btn-ghost btn-sm">Edit</Link>
              {confirmDelete ? (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Confirm'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              ) : (
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                  onClick={() => setConfirmDelete(true)}>Delete</button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      {/* Version history panel */}
      {showVersions && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Version History</span>
          </div>
          <div className="card-body">
            <VersionHistory reportId={id} onRevert={fetchReport} isAdmin={isAdmin} />
          </div>
        </div>
      )}

      {/* Comparison Chart (no filter bar — scenarios are baked into the definition) */}
      {isComparison && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Comparison</span>
            {comparisonData?.scenario_a && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {comparisonData.scenario_a.label} vs {comparisonData.scenario_b.label}
              </span>
            )}
          </div>
          <div className="card-body">
            {comparisonLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Comparing scenarios…</div>
            ) : comparisonData?.scenario_a ? (
              <ComparisonChart
                baseType={comparisonData.base_type}
                scenarioA={comparisonData.scenario_a}
                scenarioB={comparisonData.scenario_b}
                deltas={comparisonData.deltas}
              />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No data available.</div>
            )}
          </div>
        </div>
      )}

      {/* Filters (not shown for comparison reports) */}
      {!isComparison && <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-body" style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Config Version</label>
            <select className="form-input" value={configVersion}
              onChange={e => setConfigVersion(e.target.value)}
              style={{ width: '140px' }}>
              <option value="">All versions</option>
              {configVersions.map(v => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{isTimeSeries || isDuration || isActive ? 'From' : 'First seen after'}</label>
            <input className="form-input" type="date" value={from}
              onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{isTimeSeries || isDuration || isActive ? 'To' : 'First seen before'}</label>
            <input className="form-input" type="date" value={to}
              onChange={e => setTo(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={fetchData} style={{ minHeight: '36px' }}>
            Apply
          </button>
          {(from || to || configVersion) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(''); setTo(''); setConfigVersion(''); }}
              style={{ minHeight: '36px' }}>
              Clear
            </button>
          )}
        </div>
      </div>}

      {/* Time-Series Chart */}
      {isTimeSeries && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Time Series</span>
            {tsData?.series && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {tsTotalEvents.toLocaleString()} events · {tsData.series.length} series
              </span>
            )}
          </div>
          <div className="card-body">
            {tsLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading chart…</div>
            ) : tsData?.series ? (
              <TimeSeriesChart data={tsData.series} granularity={tsData.granularity} chartType={tsData.chart_type} />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No data available.</div>
            )}
          </div>
        </div>
      )}

      {/* Active / WIP */}
      {isActive && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Active Entities</span>
            {activeData?.summary && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {activeData.summary.total_active} active · {activeData.summary.total_stale} stale · {activeData.summary.total_completed} completed
              </span>
            )}
          </div>
          <div className="card-body">
            {activeLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Finding active entities…</div>
            ) : activeData?.summary ? (
              <ActiveChart
                summary={activeData.summary}
                onStepClick={idx => setActiveStepFilter(activeStepFilter === idx ? null : idx)}
                selectedStepIndex={activeStepFilter}
              />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No active data available.</div>
            )}
          </div>
        </div>
      )}

      {isActive && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">In-Flight Entities</span>
          </div>
          <div className="card-body">
            {!hasFeature('identifiers_full') ? (
              <UpgradeBanner
                feature="identifiers_full"
                currentPlan={plan?.name}
                message={`Entity drill-down requires an upgraded plan. Current plan: ${plan?.name || 'Free'}.`}
              />
            ) : (
              <ActiveEntityList
                reportId={id}
                activeData={activeData}
                from={from}
                to={to}
                configVersion={configVersion}
              />
            )}
          </div>
        </div>
      )}

      {/* Funnel */}
      {!isTimeSeries && !isDuration && !isActive && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Funnel</span>
            {funnel?.overall_completion && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Overall: {funnel.overall_completion.rate}% completion · {funnel.overall_completion.count}/{funnel.overall_completion.total}
              </span>
            )}
          </div>
          <div className="card-body">
            {funnelLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Computing funnel…</div>
            ) : funnel?.steps ? (
              <FunnelChart steps={funnel.steps} definition={def} hasTimeMetrics={hasTimeMetrics} />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No funnel data available.</div>
            )}
          </div>
        </div>
      )}

      {/* Duration Chart */}
      {isDuration && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Duration Summary</span>
            {durationData?.summary && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {durationData.summary.total_entities} entities
                {durationData.summary.overall_sla_compliance != null && (
                  <> · {durationData.summary.overall_sla_compliance}% SLA compliance</>
                )}
              </span>
            )}
          </div>
          <div className="card-body">
            {durationLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Computing durations…</div>
            ) : durationData?.transitions ? (
              <DurationChart
                transitions={durationData.transitions}
                onSelect={setSelectedTransition}
                selectedIndex={selectedTransition}
                chartType={def.display?.chart_type}
              />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No duration data available.</div>
            )}
          </div>
        </div>
      )}

      {/* Duration Transition Detail */}
      {isDuration && selectedTrans && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">{selectedTrans.label}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {selectedTrans.pair_count} entities
            </span>
          </div>
          <div className="card-body">
            {/* Stats table */}
            {selectedTrans.stats && (
              <div className="table-wrapper" style={{ marginBottom: '16px' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Average</td><td className="td-mono">{selectedTrans.stats.avg || '—'}</td></tr>
                    <tr><td>Min</td><td className="td-mono">{selectedTrans.stats.min || '—'}</td></tr>
                    <tr><td>Max</td><td className="td-mono">{selectedTrans.stats.max || '—'}</td></tr>
                    {selectedTrans.stats.percentiles && Object.entries(selectedTrans.stats.percentiles).map(([key, val]) => (
                      val && <tr key={key}><td>{key}</td><td className="td-mono">{val.formatted}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* SLA breakdown */}
            {selectedTrans.sla && (selectedTrans.sla.warning_seconds || selectedTrans.sla.breach_seconds) && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>SLA Compliance</div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {selectedTrans.sla.warning_seconds && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--warning)', display: 'inline-block' }} />
                      Warning: {formatDurationSeconds(selectedTrans.sla.warning_seconds)}
                    </span>
                  )}
                  {selectedTrans.sla.breach_seconds && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--danger)', display: 'inline-block' }} />
                      Breach: {formatDurationSeconds(selectedTrans.sla.breach_seconds)}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {selectedTrans.sla.within_warning != null && (
                    <div className="duration-sla-stat">
                      <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--warning)' }}>
                        {selectedTrans.sla.within_warning_pct}%
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        within warning ({selectedTrans.sla.within_warning}/{selectedTrans.pair_count})
                      </span>
                    </div>
                  )}
                  {selectedTrans.sla.within_breach != null && (
                    <div className="duration-sla-stat">
                      <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--success)' }}>
                        {selectedTrans.sla.within_breach_pct}%
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        within SLA ({selectedTrans.sla.within_breach}/{selectedTrans.pair_count})
                      </span>
                    </div>
                  )}
                  {selectedTrans.sla.breach_count != null && (
                    <div className="duration-sla-stat">
                      <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--danger)' }}>
                        {selectedTrans.sla.breach_count}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        breaches ({selectedTrans.sla.breach_pct}%)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Histogram for this transition */}
            {selectedTrans.histogram && selectedTrans.histogram.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Distribution</div>
                <DurationChart
                  transitions={durationData.transitions}
                  selectedIndex={selectedTransition}
                  chartType="histogram"
                />
              </div>
            )}

            {/* Breach list toggle */}
            {selectedTrans.sla && (selectedTrans.sla.breach_count > 0 || selectedTrans.sla.within_warning < selectedTrans.pair_count) && (
              <div>
                <button className={`btn btn-sm ${showBreaches ? 'btn-secondary' : 'btn-ghost'}`}
                  onClick={() => setShowBreaches(v => !v)}>
                  {showBreaches ? 'Hide' : 'Show'} Breach Details
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Breach List */}
      {isDuration && showBreaches && selectedTrans && selectedTransition != null && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Breach Details — {selectedTrans.label}</span>
          </div>
          <div className="card-body">
            <BreachList
              reportId={id}
              transitionIndex={selectedTransition}
              transition={selectedTrans}
              from={from}
              to={to}
              configVersion={configVersion}
            />
          </div>
        </div>
      )}

      {/* Entity List (funnel only) */}
      {!isTimeSeries && !isDuration && !isActive && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowEntities(v => !v)}>
            <span className="card-title">Entities {showEntities ? '▾' : '▸'}</span>
          </div>
          {showEntities && (
            <div className="card-body">
              {!hasFeature('identifiers_full') ? (
                <UpgradeBanner
                  feature="identifiers_full"
                  currentPlan={plan?.name}
                  message={`Entity drill-down requires an upgraded plan. Current plan: ${plan?.name || 'Free'}.`}
                />
              ) : (
                <EntityList
                  reportId={id}
                  stepOrder={stepOrder}
                  from={from}
                  to={to}
                  configVersion={configVersion}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
